import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import {
  Brain,
  MessageSquare,
  Lightbulb,
  Network,
  TrendingUp,
  Users,
  Settings,
  Loader2,
  Target,
  Cog,
  Clock,
  CheckCircle,
  Activity,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  confidence?: number;
  sources?: string[];
  recommendations?: Array<{
    type: string;
    content: string;
    priority: 'low' | 'medium' | 'high';
  }>;
}

interface SalesPattern {
  pattern: string;
  confidence: number;
  impact: string;
  recommendation: string;
}

interface MarketInsight {
  insight: string;
  trend: 'increasing' | 'decreasing' | 'stable';
  confidence: number;
}

export default function AIAgentPage() {
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [sessionId, setSessionId] = useState(`session_${Date.now()}`);
  const [agentResults, setAgentResults] = useState<
    Array<{
      agent: string;
      action: string;
      result: any;
      timestamp: Date;
    }>
  >([]);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch customers for selection
  const { data: customersResponse } = useQuery({
    queryKey: ['/api/customers?legacy=true'],
    queryFn: async () => {
      const response = await fetch('/api/customers?legacy=true');
      if (!response.ok) throw new Error('Failed to fetch customers');
      return response.json();
    },
  });

  // Handle both paginated and legacy responses
  const customers = Array.isArray(customersResponse)
    ? customersResponse
    : customersResponse?.data || [];

  // Chat with AI agent
  const chatMutation = useMutation({
    mutationFn: async ({ message, customerId }: { message: string; customerId?: number }) => {
      const response = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          customerId,
          sessionId,
          agentType: 'support',
        }),
      });
      if (!response.ok) throw new Error('Failed to send message');
      return response.json();
    },
    onSuccess: (data) => {
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: data.response,
        timestamp: new Date(),
        confidence: data.confidence,
        sources: data.sources,
        recommendations: data.recommendations,
      };
      setChatMessages((prev) => [...prev, assistantMessage]);
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to process your message. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Fetch sales patterns
  const { data: salesPatternsData, isLoading: loadingSalesPatterns } = useQuery({
    queryKey: ['/api/ai/sales-patterns'],
    queryFn: async () => {
      const response = await fetch('/api/ai/sales-patterns');
      if (!response.ok) throw new Error('Failed to fetch sales patterns');
      return response.json();
    },
  });

  // Initialize AI systems
  const initializeAIMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch('/api/ai/initialize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to initialize AI systems');
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'AI memory and knowledge systems initialized successfully',
      });
      queryClient.invalidateQueries();
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to initialize AI systems. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Build knowledge graph
  const buildKnowledgeGraphMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch('/api/knowledge-graph/build');
      if (!response.ok) throw new Error('Failed to build knowledge graph');
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Knowledge graphs built successfully',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to build knowledge graph. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const handleSendMessage = () => {
    if (!currentMessage.trim()) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: currentMessage,
      timestamp: new Date(),
    };

    setChatMessages((prev) => [...prev, userMessage]);
    chatMutation.mutate({ message: currentMessage, customerId: selectedCustomerId || undefined });
    setCurrentMessage('');
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return '📈';
      case 'decreasing':
        return '📉';
      case 'stable':
        return '➡️';
      default:
        return '📊';
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">Enhanced AI Agent - Phase 1</h1>
        <p className="text-gray-600">Advanced Memory & Knowledge Foundation</p>
        <div className="flex justify-center gap-4 mt-4">
          <Button
            onClick={() => initializeAIMutation.mutate()}
            disabled={initializeAIMutation.isPending}
            className="flex items-center gap-2"
          >
            {initializeAIMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Brain className="h-4 w-4" />
            )}
            Initialize AI Systems
          </Button>
          <Button
            onClick={() => buildKnowledgeGraphMutation.mutate()}
            disabled={buildKnowledgeGraphMutation.isPending}
            variant="outline"
            className="flex items-center gap-2"
          >
            {buildKnowledgeGraphMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Network className="h-4 w-4" />
            )}
            Build Knowledge Graph
          </Button>
        </div>
      </div>

      <Tabs defaultValue="chat" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="chat" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            AI Chat
          </TabsTrigger>
          <TabsTrigger value="agents" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Phase 3 Agents
          </TabsTrigger>
          <TabsTrigger value="patterns" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Sales Patterns
          </TabsTrigger>
          <TabsTrigger value="memory" className="flex items-center gap-2">
            <Brain className="h-4 w-4" />
            Memory System
          </TabsTrigger>
          <TabsTrigger value="knowledge" className="flex items-center gap-2">
            <Network className="h-4 w-4" />
            Knowledge Graph
          </TabsTrigger>
        </TabsList>

        <TabsContent value="chat" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Context-Aware AI Chat
              </CardTitle>
              <CardDescription>
                Chat with the AI agent that has access to customer memory and knowledge graphs
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="text-sm font-medium">Select Customer (Optional)</label>
                  <select
                    className="w-full mt-1 p-2 border rounded-md"
                    value={selectedCustomerId || ''}
                    onChange={(e) =>
                      setSelectedCustomerId(e.target.value ? parseInt(e.target.value) : null)
                    }
                  >
                    <option value="">No specific customer</option>
                    {customers.map((customer: any) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name} - {customer.email}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="h-96 border rounded-lg p-4 overflow-y-auto bg-gray-50">
                {chatMessages.length === 0 ? (
                  <div className="text-center text-gray-500 mt-20">
                    <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Start a conversation with the AI agent</p>
                    <p className="text-sm">
                      Try asking about solar installations, customer preferences, or technical
                      recommendations
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {chatMessages.map((message, index) => (
                      <div
                        key={index}
                        className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-3xl p-3 rounded-lg ${
                            message.role === 'user' ? 'bg-blue-500 text-white' : 'bg-white border'
                          }`}
                        >
                          <p>{message.content}</p>
                          {message.confidence && (
                            <div className="mt-2 text-xs opacity-75">
                              Confidence: {Math.round(message.confidence * 100)}%
                            </div>
                          )}
                          {message.sources && message.sources.length > 0 && (
                            <div className="mt-2 flex gap-1 flex-wrap">
                              {message.sources.map((source, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {source}
                                </Badge>
                              ))}
                            </div>
                          )}
                          {message.recommendations && message.recommendations.length > 0 && (
                            <div className="mt-2 space-y-1">
                              <p className="text-xs font-medium">Recommendations:</p>
                              {message.recommendations.map((rec, i) => (
                                <div
                                  key={i}
                                  className={`text-xs p-2 rounded ${getPriorityColor(rec.priority)}`}
                                >
                                  <strong>{rec.type}:</strong> {rec.content}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Textarea
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  placeholder="Ask about solar installations, customer preferences, technical recommendations..."
                  className="flex-1"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={chatMutation.isPending || !currentMessage.trim()}
                  className="px-8"
                >
                  {chatMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="agents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Phase 3: Multi-Agent System
              </CardTitle>
              <CardDescription>
                Test the specialized AI agents that work together to handle complex business
                processes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Sales Intelligence Agent */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Sales Intelligence Agent
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Lead Scoring & Analysis</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/sales-intelligence/analyze-leads', { method: 'POST' })
                            .then((res) => res.json())
                            .then((data) => {
                              const result = {
                                agent: 'Sales Intelligence',
                                action: 'Lead Analysis',
                                result: data,
                                timestamp: new Date(),
                              };
                              setAgentResults((prev) => [result, ...prev.slice(0, 9)]);
                              toast({
                                title: 'Sales Analysis Complete',
                                description: `Analyzed ${data.leadsAnalyzed} leads with average score ${data.averageScore}`,
                              });
                            });
                        }}
                      >
                        Analyze All Leads
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/sales-intelligence/market-trends', { method: 'POST' })
                            .then((res) => res.json())
                            .then((data) => {
                              const result = {
                                agent: 'Sales Intelligence',
                                action: 'Market Trends Analysis',
                                result: data,
                                timestamp: new Date(),
                              };
                              setAgentResults((prev) => [result, ...prev.slice(0, 9)]);
                              toast({
                                title: 'Market Analysis Complete',
                                description: `Identified ${data.trendsFound} market trends`,
                              });
                            });
                        }}
                      >
                        Generate Market Insights
                      </Button>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Sales Optimization</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/sales-intelligence/optimize-pipeline', {
                            method: 'POST',
                          })
                            .then((res) => res.json())
                            .then((data) => {
                              const result = {
                                agent: 'Sales Intelligence',
                                action: 'Pipeline Optimization',
                                result: data,
                                timestamp: new Date(),
                              };
                              setAgentResults((prev) => [result, ...prev.slice(0, 9)]);
                              toast({
                                title: 'Pipeline Optimized',
                                description: `${data.optimizations} improvements suggested`,
                              });
                            });
                        }}
                      >
                        Optimize Pipeline
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/sales-intelligence/predict-conversions', {
                            method: 'POST',
                          })
                            .then((res) => res.json())
                            .then((data) => {
                              const result = {
                                agent: 'Sales Intelligence',
                                action: 'Conversion Prediction',
                                result: data,
                                timestamp: new Date(),
                              };
                              setAgentResults((prev) => [result, ...prev.slice(0, 9)]);
                              toast({
                                title: 'Conversion Predictions Ready',
                                description: `${data.predictions} conversion predictions generated`,
                              });
                            });
                        }}
                      >
                        Predict Conversions
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Technical Advisory Agent */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Cog className="h-4 w-4" />
                  Technical Advisory Agent
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">System Assessment</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/technical-advisory/assess-property', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ propertyId: selectedCustomerId }),
                          })
                            .then((res) => res.json())
                            .then((data) => {
                              toast({
                                title: 'Property Assessment Complete',
                                description: `Technical feasibility: ${data.feasibilityScore}%`,
                              });
                            });
                        }}
                        disabled={!selectedCustomerId}
                      >
                        Assess Selected Property
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/technical-advisory/design-recommendations', {
                            method: 'POST',
                          })
                            .then((res) => res.json())
                            .then((data) => {
                              toast({
                                title: 'Design Recommendations Ready',
                                description: `${data.recommendations} system designs suggested`,
                              });
                            });
                        }}
                      >
                        Generate Design Options
                      </Button>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Performance Analysis</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/technical-advisory/calculate-roi', { method: 'POST' })
                            .then((res) => res.json())
                            .then((data) => {
                              toast({
                                title: 'ROI Analysis Complete',
                                description: `Average ROI: ${data.averageROI}% over ${data.paybackPeriod} years`,
                              });
                            });
                        }}
                      >
                        Calculate ROI
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/technical-advisory/optimize-efficiency', {
                            method: 'POST',
                          })
                            .then((res) => res.json())
                            .then((data) => {
                              toast({
                                title: 'Efficiency Optimized',
                                description: `${data.improvements}% efficiency improvement possible`,
                              });
                            });
                        }}
                      >
                        Optimize Efficiency
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Project Coordination Agent */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Project Coordination Agent
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Scheduling & Planning</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/project-coordination/schedule-installation', {
                            method: 'POST',
                          })
                            .then((res) => res.json())
                            .then((data) => {
                              toast({
                                title: 'Installation Scheduled',
                                description: `${data.scheduledProjects} projects scheduled optimally`,
                              });
                            });
                        }}
                      >
                        Optimize Schedules
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/project-coordination/manage-resources', {
                            method: 'POST',
                          })
                            .then((res) => res.json())
                            .then((data) => {
                              toast({
                                title: 'Resources Optimized',
                                description: `${data.efficiency}% resource efficiency achieved`,
                              });
                            });
                        }}
                      >
                        Manage Resources
                      </Button>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Progress Tracking</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/project-coordination/track-progress', {
                            method: 'POST',
                          })
                            .then((res) => res.json())
                            .then((data) => {
                              toast({
                                title: 'Progress Updated',
                                description: `${data.activeProjects} projects tracked, ${data.onSchedule}% on schedule`,
                              });
                            });
                        }}
                      >
                        Update Project Status
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/project-coordination/predict-delays', {
                            method: 'POST',
                          })
                            .then((res) => res.json())
                            .then((data) => {
                              toast({
                                title: 'Delay Predictions Ready',
                                description: `${data.riskProjects} projects at risk identified`,
                              });
                            });
                        }}
                      >
                        Predict Delays
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Compliance Monitor Agent */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Compliance Monitor Agent
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Regulatory Compliance</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/compliance-monitor/check-regulations', {
                            method: 'POST',
                          })
                            .then((res) => res.json())
                            .then((data) => {
                              toast({
                                title: 'Compliance Check Complete',
                                description: `${data.compliantProjects}/${data.totalProjects} projects compliant`,
                              });
                            });
                        }}
                      >
                        Check All Regulations
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/compliance-monitor/verify-documents', {
                            method: 'POST',
                          })
                            .then((res) => res.json())
                            .then((data) => {
                              toast({
                                title: 'Document Verification Complete',
                                description: `${data.verifiedDocs}/${data.totalDocs} documents verified`,
                              });
                            });
                        }}
                      >
                        Verify Documents
                      </Button>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Quality Assurance</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/compliance-monitor/audit-installations', {
                            method: 'POST',
                          })
                            .then((res) => res.json())
                            .then((data) => {
                              toast({
                                title: 'Installation Audit Complete',
                                description: `${data.auditScore}% average quality score`,
                              });
                            });
                        }}
                      >
                        Audit Installations
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/compliance-monitor/safety-check', { method: 'POST' })
                            .then((res) => res.json())
                            .then((data) => {
                              toast({
                                title: 'Safety Check Complete',
                                description: `${data.safetyIssues} safety issues identified and flagged`,
                              });
                            });
                        }}
                      >
                        Safety Assessment
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Customer Success Agent */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Customer Success Agent
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Customer Journey</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/customer-success/analyze-satisfaction', {
                            method: 'POST',
                          })
                            .then((res) => res.json())
                            .then((data) => {
                              toast({
                                title: 'Satisfaction Analysis Complete',
                                description: `${data.averageSatisfaction}% average satisfaction score`,
                              });
                            });
                        }}
                      >
                        Analyze Satisfaction
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/customer-success/predict-churn', { method: 'POST' })
                            .then((res) => res.json())
                            .then((data) => {
                              toast({
                                title: 'Churn Prediction Complete',
                                description: `${data.riskCustomers} customers at risk identified`,
                              });
                            });
                        }}
                      >
                        Predict Churn Risk
                      </Button>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Engagement & Retention</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/customer-success/personalize-communication', {
                            method: 'POST',
                          })
                            .then((res) => res.json())
                            .then((data) => {
                              toast({
                                title: 'Communications Personalized',
                                description: `${data.personalizedMessages} personalized messages created`,
                              });
                            });
                        }}
                      >
                        Personalize Communications
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/customer-success/generate-upsells', { method: 'POST' })
                            .then((res) => res.json())
                            .then((data) => {
                              toast({
                                title: 'Upsell Opportunities Found',
                                description: `${data.opportunities} upsell opportunities identified`,
                              });
                            });
                        }}
                      >
                        Find Upsell Opportunities
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Multi-Agent Collaboration */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Network className="h-4 w-4" />
                  Multi-Agent Collaboration
                </h3>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Coordinated Workflows</CardTitle>
                    <CardDescription className="text-xs">
                      Test how multiple agents work together on complex tasks
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="grid md:grid-cols-3 gap-2">
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/collaboration/complete-customer-journey', {
                            method: 'POST',
                          })
                            .then((res) => res.json())
                            .then((data) => {
                              toast({
                                title: 'Customer Journey Completed',
                                description: `${data.stagesCompleted} workflow stages completed by ${data.agentsInvolved} agents`,
                              });
                            });
                        }}
                      >
                        End-to-End Journey
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/collaboration/optimize-operations', { method: 'POST' })
                            .then((res) => res.json())
                            .then((data) => {
                              toast({
                                title: 'Operations Optimized',
                                description: `${data.improvementAreas} areas optimized across all agents`,
                              });
                            });
                        }}
                      >
                        Optimize Operations
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          fetch('/api/phase3/collaboration/generate-insights', { method: 'POST' })
                            .then((res) => res.json())
                            .then((data) => {
                              toast({
                                title: 'Cross-Agent Insights Generated',
                                description: `${data.insights} insights from agent collaboration`,
                              });
                            });
                        }}
                      >
                        Generate Insights
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Results Display Area */}
              {agentResults.length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    Recent Agent Analysis Results
                  </h3>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {agentResults.map((result, index) => (
                      <Card key={index} className="bg-gray-50">
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-start">
                            <CardTitle className="text-sm">
                              {result.agent} - {result.action}
                            </CardTitle>
                            <Badge variant="outline" className="text-xs">
                              {result.timestamp.toLocaleTimeString()}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="text-sm text-gray-600">{result.result.message}</div>

                          {/* Show insights if available */}
                          {result.result.insights && (
                            <div className="space-y-2">
                              <h4 className="font-medium text-sm">Key Insights:</h4>
                              <ul className="space-y-1 text-xs">
                                {result.result.insights.map((insight, idx) => (
                                  <li key={idx} className="flex items-start gap-2">
                                    <span className="text-blue-500 mt-1">•</span>
                                    <span>{insight}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Show detailed metrics */}
                          <div className="grid grid-cols-1 gap-3 text-xs">
                            {Object.entries(result.result).map(([key, value]) => {
                              if (key === 'success' || key === 'message' || key === 'insights')
                                return null;

                              // Handle array/object data
                              if (Array.isArray(value)) {
                                return (
                                  <div key={key} className="space-y-1">
                                    <span className="font-medium capitalize">
                                      {key.replace(/([A-Z])/g, ' $1')}:
                                    </span>
                                    <div className="ml-2 space-y-1">
                                      {value.slice(0, 3).map((item, idx) => (
                                        <div key={idx} className="text-gray-600">
                                          {typeof item === 'object'
                                            ? JSON.stringify(item, null, 2)
                                            : String(item)}
                                        </div>
                                      ))}
                                      {value.length > 3 && (
                                        <div className="text-gray-500">
                                          ...and {value.length - 3} more
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              }

                              // Handle object data
                              if (typeof value === 'object' && value !== null) {
                                return (
                                  <div key={key} className="space-y-1">
                                    <span className="font-medium capitalize">
                                      {key.replace(/([A-Z])/g, ' $1')}:
                                    </span>
                                    <div className="ml-2 grid grid-cols-2 gap-2 text-xs">
                                      {Object.entries(value).map(([subKey, subValue]) => (
                                        <div key={subKey}>
                                          <span className="text-gray-500">{subKey}:</span>{' '}
                                          {String(subValue)}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              }

                              // Handle simple values
                              return (
                                <div key={key} className="flex justify-between">
                                  <span className="font-medium capitalize">
                                    {key.replace(/([A-Z])/g, ' $1')}:
                                  </span>
                                  <span>{String(value)}</span>
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="patterns" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Sales Pattern Analysis
              </CardTitle>
              <CardDescription>
                AI-generated insights from sales data and customer behavior patterns
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingSalesPatterns ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                  <span className="ml-2">Analyzing sales patterns...</span>
                </div>
              ) : (
                <div className="space-y-6">
                  {salesPatternsData?.patterns && Array.isArray(salesPatternsData.patterns) && (
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Identified Patterns</h3>
                      <div className="space-y-4">
                        {salesPatternsData.patterns.map((pattern: SalesPattern, index: number) => (
                          <Card key={index} className="p-4">
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="font-medium">{pattern.pattern}</h4>
                              <Badge className="bg-blue-100 text-blue-800">
                                {Math.round(pattern.confidence * 100)}% confidence
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{pattern.impact}</p>
                            <div className="bg-green-50 p-3 rounded border-l-4 border-green-400">
                              <p className="text-sm">
                                <strong>Recommendation:</strong> {pattern.recommendation}
                              </p>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}

                  {salesPatternsData?.marketInsights &&
                    Array.isArray(salesPatternsData.marketInsights) && (
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Market Insights</h3>
                        <div className="grid gap-4 md:grid-cols-2">
                          {salesPatternsData.marketInsights.map(
                            (insight: MarketInsight, index: number) => (
                              <Card key={index} className="p-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-lg">{getTrendIcon(insight.trend)}</span>
                                  <h4 className="font-medium">{insight.insight}</h4>
                                </div>
                                <div className="flex justify-between items-center">
                                  <Badge
                                    variant="outline"
                                    className={
                                      insight.trend === 'increasing'
                                        ? 'border-green-300 text-green-700'
                                        : insight.trend === 'decreasing'
                                          ? 'border-red-300 text-red-700'
                                          : 'border-gray-300 text-gray-700'
                                    }
                                  >
                                    {insight.trend}
                                  </Badge>
                                  <span className="text-sm text-gray-500">
                                    {Math.round(insight.confidence * 100)}% confidence
                                  </span>
                                </div>
                              </Card>
                            )
                          )}
                        </div>
                      </div>
                    )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="memory" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5" />
                Memory Management System
              </CardTitle>
              <CardDescription>
                Track and analyze customer interactions, preferences, and project insights
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <Brain className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-semibold mb-2">Memory System Active</h3>
                <p className="text-gray-600 mb-4">
                  The memory system is actively tracking customer interactions and building
                  knowledge.
                </p>
                <div className="grid gap-4 md:grid-cols-3 max-w-2xl mx-auto">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-800">Customer Memory</h4>
                    <p className="text-sm text-blue-600">Conversations, preferences, objections</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="font-medium text-green-800">Project Memory</h4>
                    <p className="text-sm text-green-600">Technical specs, challenges, solutions</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h4 className="font-medium text-purple-800">Business Memory</h4>
                    <p className="text-sm text-purple-600">Sales patterns, market trends</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="knowledge" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Network className="h-5 w-5" />
                Knowledge Graph System
              </CardTitle>
              <CardDescription>
                Real-time relationship mapping and contextual recommendations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <Network className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-semibold mb-2">Knowledge Graph Built</h3>
                <p className="text-gray-600 mb-4">
                  Connected relationships between customers, components, processes, and external
                  data.
                </p>
                <div className="grid gap-4 md:grid-cols-2 max-w-2xl mx-auto">
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <h4 className="font-medium text-orange-800">Customer Relationships</h4>
                    <p className="text-sm text-orange-600">
                      Property connections, referral networks
                    </p>
                  </div>
                  <div className="p-4 bg-teal-50 rounded-lg">
                    <h4 className="font-medium text-teal-800">Technical Knowledge</h4>
                    <p className="text-sm text-teal-600">
                      Component compatibility, installation methods
                    </p>
                  </div>
                  <div className="p-4 bg-indigo-50 rounded-lg">
                    <h4 className="font-medium text-indigo-800">Business Processes</h4>
                    <p className="text-sm text-indigo-600">Pipeline stages, dependencies</p>
                  </div>
                  <div className="p-4 bg-pink-50 rounded-lg">
                    <h4 className="font-medium text-pink-800">External Data</h4>
                    <p className="text-sm text-pink-600">
                      OpenSolar integration, regulatory compliance
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
